import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import type { RootState } from "@/store"
import type {
  Sage300ConnectionDto,
  Sage300UpsertRequestDto,
} from "../dtos/Sage300ConnectDto"
import {
  getConnection,
  upsertConnection,
  deleteConnection,
} from "../infrastructure/sage300ConnectorService"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"

function getErrorMessage(err: unknown, fallback: string) {
  if (typeof err === "object" && err !== null) {
    const maybeResponse = (err as { response?: unknown }).response
    if (typeof maybeResponse === "object" && maybeResponse !== null) {
      const maybeData = (maybeResponse as { data?: unknown }).data
      if (typeof maybeData === "object" && maybeData !== null) {
        const maybeMessage = (maybeData as { message?: unknown }).message
        if (typeof maybeMessage === "string") return maybeMessage
      }
    }
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

interface Sage300ConnectorState {
  connection: Sage300ConnectionDto | null
  isLoading: boolean
  isSubmitting: boolean
  isDeleting: boolean
  error: string | null
  isSuccess: boolean
}

const initialState: Sage300ConnectorState = {
  connection: null,
  isLoading: false,
  isSubmitting: false,
  isDeleting: false,
  error: null,
  isSuccess: false,
}

export const fetchSage300ConnectionThunk = createAsyncThunk<
  Sage300ConnectionDto | null,
  void,
  { state: RootState; rejectValue: string }
>(
  "profile/sage300/fetchConnection",
  async (_, { getState, rejectWithValue }) => {
    try {
      const orgId = selectCurrentOrganizationId(getState())
      return await getConnection(orgId)
    } catch (err: unknown) {
      return rejectWithValue(
        getErrorMessage(err, "Failed to load Sage 300 connection"),
      )
    }
  },
)

export const upsertSage300ConnectionThunk = createAsyncThunk<
  Sage300ConnectionDto,
  Sage300UpsertRequestDto,
  { state: RootState; rejectValue: string }
>(
  "profile/sage300/upsertConnection",
  async (dto, { getState, rejectWithValue }) => {
    try {
      const orgId = selectCurrentOrganizationId(getState())
      const result = await upsertConnection(orgId, dto)
      if (result.isSuccess === false) {
        return rejectWithValue(
          result.error ?? "Sage 300 connection validation failed",
        )
      }
      return dto
    } catch (err: unknown) {
      return rejectWithValue(
        getErrorMessage(err, "Failed to save Sage 300 connection"),
      )
    }
  },
)

export const deleteSage300ConnectionThunk = createAsyncThunk<
  void,
  void,
  { state: RootState; rejectValue: string }
>(
  "profile/sage300/deleteConnection",
  async (_, { getState, rejectWithValue }) => {
    try {
      const orgId = selectCurrentOrganizationId(getState())
      await deleteConnection(orgId)
    } catch (err: unknown) {
      return rejectWithValue(
        getErrorMessage(err, "Failed to delete Sage 300 connection"),
      )
    }
  },
)

const sage300ConnectorSlice = createSlice({
  name: "profileSage300Connector",
  initialState,
  reducers: {
    clearSage300ConnectorError(state) {
      state.error = null
    },
    clearSage300ConnectorState() {
      return initialState
    },
    resetSage300Success(state) {
      state.isSuccess = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSage300ConnectionThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchSage300ConnectionThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.connection = action.payload
      })
      .addCase(fetchSage300ConnectionThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload ?? "Failed to load Sage 300 connection"
      })

      .addCase(upsertSage300ConnectionThunk.pending, (state) => {
        state.isSubmitting = true
        state.error = null
        state.isSuccess = false
      })
      .addCase(upsertSage300ConnectionThunk.fulfilled, (state, action) => {
        state.isSubmitting = false
        state.connection = action.payload
        state.isSuccess = true
      })
      .addCase(upsertSage300ConnectionThunk.rejected, (state, action) => {
        state.isSubmitting = false
        state.error = action.payload ?? "Failed to save Sage 300 connection"
      })

      .addCase(deleteSage300ConnectionThunk.pending, (state) => {
        state.isDeleting = true
        state.error = null
      })
      .addCase(deleteSage300ConnectionThunk.fulfilled, (state) => {
        state.isDeleting = false
        state.connection = null
      })
      .addCase(deleteSage300ConnectionThunk.rejected, (state, action) => {
        state.isDeleting = false
        state.error = action.payload ?? "Failed to delete Sage 300 connection"
      })

      .addMatcher(
        (action) => action.type === "organizations/setCurrentOrganizationId",
        () => initialState,
      )
  },
})

export const {
  clearSage300ConnectorError,
  clearSage300ConnectorState,
  resetSage300Success,
} = sage300ConnectorSlice.actions

export default sage300ConnectorSlice.reducer

export const selectSage300Connection = (state: RootState) =>
  state.sage300Connector.connection
export const selectSage300IsLoading = (state: RootState) =>
  state.sage300Connector.isLoading
export const selectSage300IsSubmitting = (state: RootState) =>
  state.sage300Connector.isSubmitting
export const selectSage300Error = (state: RootState) =>
  state.sage300Connector.error
export const selectSage300IsSuccess = (state: RootState) =>
  state.sage300Connector.isSuccess
export const selectSage300IsDeleting = (state: RootState) =>
  state.sage300Connector.isDeleting
